/*
 * pic-nn-web-ui
 *
 * Web UI for neural network applications.
 *
 * @copyright 2020 VSTE CB <http://www.vstecb.cz/>.
 * @license See LICENSE file distributed with this source code for more information.
 */

import { ModelHandler, TModelHandlerInputMap, TModelHandlerOutputMap } from "./ModelHandler";
import { EventEmitter } from "../EventEmitter";
import { IModelOutputState } from "./IModelOutputState";

export interface IModelOutputResultLabel<ValueType> {
	/** Match condition */
	condition: (
		state: IModelOutputState<ValueType, IModelOutputBaseConfig>,
		handler: ModelHandler<any, any>
	) => boolean;

	/** Prefix label */
	prefix: string;

	/** Content text (markdown-enabled) */
	content: string;

	/** If prefix is large */
	large?: boolean;
}

export interface IModelOutputBaseConfig<ValueType = any> {
	/** Suffix label, eg. for unit or currency */
	suffix?: string;

	/** Input label */
	label?: string;

	/** More description */
	description?: string;

	/** Result labels */
	resultLabels?: Array<IModelOutputResultLabel<ValueType>>;

	/** Number of decimals to format number to */
	decimals?: number;

	/** Custom multiplier (eg. for percentage) */
	multiplier?: number;
}

/**
 * Model Output Field Base Class
 */
export abstract class ModelOutput<ValueType, ConfigType extends IModelOutputBaseConfig<ValueType> = IModelOutputBaseConfig>
	extends EventEmitter<"update", IModelOutputState<ValueType, ConfigType>> {

	/** Model handler */
	protected handler: ModelHandler;

	/** Field type */
	private type: string;

	/** Output value */
	private value: ValueType;

	/** Additional field configuration */
	protected config: ConfigType;

	/** If the value is loading */
	private loading: boolean = false;

	/** If the value should be re-updated (eg. model was changed during async update) */
	private shouldUpdate: boolean = false;

	/** If the field is in an error state */
	private error: boolean = false;

	/** Last known error */
	private lastError: Error = null;

	/**
	 * Field constructor
	 *
	 * @param handler Handler instance
	 * @param type Field type
	 * @param initialValue Initial value
	 */
	public constructor(
		handler: ModelHandler,
		type: string,
		initialValue: ValueType,
		config: ConfigType
	) {

		super();

		this.handler = handler;
		this.type = type;
		this.config = config;
		this.value = initialValue;

	}

	/**
	 * Computes the output
	 */
	protected abstract compute(inputs: TModelHandlerInputMap, outputs: TModelHandlerOutputMap): ValueType|Promise<ValueType>;

	/**
	 * Updates the output value
	 */
	public update() {

		// Set should update when already loading
		if (this.loading) {
			this.shouldUpdate = true;
			return;
		}

		const value = this.compute(this.handler.inputs, this.handler.outputs);

		if (value instanceof Promise) {

			this.loading = true;
			this.emit("update", this.getState());

			value.then((newValue) => {

				this.value = newValue;
				this.error = false;
				this.loading = false;

				if (this.shouldUpdate) {
					this.shouldUpdate = false;
					this.update();
				} else {
					this.emit("update", this.getState());
				}

			}, (err) => {

				this.error = true;
				this.lastError = err;
				this.loading = false;

				if (this.shouldUpdate) {
					this.shouldUpdate = false;
					this.update();
				} else {
					this.emit("update", this.getState());
				}

			});

		} else {

			this.value = value;
			this.loading = false;
			this.error = false;

			this.emit("update", this.getState());

		}

	}

	/**
	 * Returns output value
	 */
	public get() {

		return this.value;

	}

	/**
	 * Returns field specification for UI
	 */
	public getState(): IModelOutputState<ValueType, ConfigType> {

		return {
			type: this.type,
			value: this.value,
			config: this.config,
			loading: this.loading,
			error: this.error,
			lastError: this.lastError
		};

	}

	/**
	 * Returns the field handler
	 */
	public getHandler() {

		return this.handler;

	}

}
